import { useCallback, useEffect, useRef } from "react";
import { LocalMessageSubPub, useLocalMessagingContext, FilterHelper as Flt } from "../LocalMessagingRoot"


const GLOBAL_DISABLED_MSG_TOPIC = 'OD_LM_GLOBAL_DISABLED_MSG_TOPIC'
const INQUIRY_GLOBAL_DISABLED_REQ_TOPIC = 'OD_LM_INQUIRY_GLOBAL_DISABLED_REQ_TOPIC'

interface GlobalDisabledMsgActions {
  sendDownDisabledMsg: (disabled: boolean) => void;
  disabled: boolean|undefined;
}

interface LmGlobalDisabledMsgProps {
  listener?: (disabled: boolean) => void;
  authority?: () => Promise<boolean>;
}

interface GlobalDisabledMsgPayload {
  disabled: boolean
}

export const useLmGlobalDisabledMsg = ({listener, authority}: LmGlobalDisabledMsgProps, subPub?: LocalMessageSubPub): GlobalDisabledMsgActions => {
  const ctx = useLocalMessagingContext()
  const subscribe = subPub?.subscribe ?? ctx.subscribe
  const publish = subPub?.publish ?? ctx.publish
  if (subscribe == null || publish == null) { throw 'Should use this hook inside a LocalMessagingContext' }

  const { current: refBag } = useRef({listener, disabled: undefined as boolean|undefined})
  refBag.listener = listener

  const sendDownDisabledMsg = useCallback((disabled: boolean) => {
    publish({
      topic: GLOBAL_DISABLED_MSG_TOPIC,
      payload: { disabled: !!disabled }
    })
  }, [publish])

  useEffect(() => {
    return subscribe(
      !refBag.listener 
      ? [Flt.withRelativeAddress.None]
      : [Flt.withTopic(GLOBAL_DISABLED_MSG_TOPIC), Flt.withRelativeAddress.Ancestors]
      ,
      (m) => {
        const msg = m.payload as GlobalDisabledMsgPayload
        if (refBag.listener) {
          refBag.disabled = msg.disabled
          refBag.listener(msg.disabled)
        }
      }
    )
  }, [subscribe, refBag])

  useEffect(() => {
    return subscribe(
      !authority
      ? [Flt.withRelativeAddress.None]
      : [Flt.withTopic(INQUIRY_GLOBAL_DISABLED_REQ_TOPIC), Flt.withRelativeAddress.All],
      (m) => {
        if (authority) {
          authority().then((disabled) => {
            publish({
              topic: GLOBAL_DISABLED_MSG_TOPIC,
              payload: { disabled: !!disabled }
            }, m.fromAddress)
          })
        }
      }
    )
  }, [subscribe, authority, publish])

  useEffect(() => {
    if (refBag.listener && refBag.disabled === undefined) {
      publish({
        topic: INQUIRY_GLOBAL_DISABLED_REQ_TOPIC,
        payload: 1
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refBag.listener, publish])

  return {sendDownDisabledMsg, disabled: refBag.disabled}
}
